import { createContext, useMemo, useContext, useCallback, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router';

import { checkRol } from '../security';
import { aprobador, solicitante } from '../security/constantsRols';
import { getfilterPreference, postFilterPreference } from '../services/serviceRequest';
import { getfilterPreferenceApprover, postFilterPreferenceApprover } from '../services/workReport';
import useToast from '../hooks/useToast';
import useAsync from '../hooks/useAsync';
import {
  postFilterPreferenceApproverAdapter,
  postFiltersPreferencesAdapter
} from '../adapters/filters/postFiltersPreferences';
import {
  approverFiltersAdapterToResponse,
  filtersAdapter
} from '../adapters/filters/getFiltersPreferences';
import { chipsWordsForEventAsignment, eventAsignmentEnum } from '../utils/dataStatus';

const FilterContext = createContext();

export const useFilterContext = () => useContext(FilterContext);

const FilterProvider = ({ children }) => {
  const { setNewToast } = useToast();
  const location = useLocation();
  const approverRoute = location.pathname === '/approver';
  const isApprover = approverRoute && checkRol(aprobador);
  const isApplicant = !approverRoute && checkRol(solicitante);

  const initialFiltersPrefrencesValues = useMemo(
    () => ({
      asignacionEventoEnum: eventAsignmentEnum.all,
      equipo: [],
      estadosEnums: [],
      evento: '',
      fechaDesde: '',
      fechaHasta: '',
      idPrioridad: '',
      idsContratos: [],
      idsProveedores: [],
      idsServicios: [],
      idUsuarioCreador: '',
      isApprover,
      numeroSolicitud: '',
      operacionesIds: [],
      tipoUbicacionEnum: null,
      ubicacion: [],
      unidadesNegocioIds: [],
      tipoEvento: [],
      posicionesLineas: [],
      posicionesLineasChanges: [],
      numerosSolicitudes: [],
      estadosSolicitud: [],
      isPending: true
    }),
    [isApprover]
  );

  const getFiltersPreferencesFromDB = useCallback(async () => {
    try {
      if (approverRoute) {
        const parsedFilters = await getfilterPreferenceApprover();
        const newParsedFilters = approverFiltersAdapterToResponse(parsedFilters);

        return newParsedFilters;
      }

      return filtersAdapter(await getfilterPreference());
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });

      return initialFiltersPrefrencesValues;
    }
  }, [approverRoute, setNewToast, initialFiltersPrefrencesValues]);

  const getFiltersPreferencesFromLocalStorage = useCallback(() => {
    const filters = window.localStorage.getItem(
      isApprover ? 'filtersApproverWorkReport' : 'filtersRequestGrid'
    );
    const objectOfFilters = JSON.parse(filters);

    if (
      !filters ||
      (isApprover && !objectOfFilters.isApprover) ||
      (isApplicant && objectOfFilters.isApprover)
    )
      return null;

    return objectOfFilters;
  }, [isApplicant, isApprover]);

  const initializeFiltersPreferences = useCallback(async () => {
    const filtersFromLocalStorage = getFiltersPreferencesFromLocalStorage();

    if (!filtersFromLocalStorage) {
      const filtersFromDB = await getFiltersPreferencesFromDB();

      window.localStorage.setItem(
        isApprover ? 'filtersApproverWorkReport' : 'filtersRequestGrid',
        JSON.stringify({ ...filtersFromDB, isApprover })
      );

      return filtersFromDB;
    }

    return filtersFromLocalStorage;
  }, [getFiltersPreferencesFromDB, getFiltersPreferencesFromLocalStorage, isApprover]);

  const { data: filtersPreferences } = useAsync(initializeFiltersPreferences);

  // TODO
  // Este estado esta acoplado al hook useSdsGrilla ya que es el que se encarga
  // de hacer el fetch de la data cuando se aplican filtros. Debería suceder en el
  //  onSubmit de useFormik de este Provider
  const [filtersPreferencesState, setFiltersPreferencesState] = useState(
    initialFiltersPrefrencesValues
  );

  const formikFilters = useFormik({
    initialValues: filtersPreferences ?? initialFiltersPrefrencesValues,
    onSubmit: ({ posicionesLineasChanges, ...rest }) => {
      const values = { ...rest, posicionesLineas: posicionesLineasChanges };

      setFiltersPreferencesState(values);

      window.localStorage.setItem(
        isApprover ? 'filtersApproverWorkReport' : 'filtersRequestGrid',
        JSON.stringify({ ...values, isApprover })
      );
    },
    enableReinitialize: true
  });

  const updateFiltersPreferences = useCallback(
    async (filters) => {
      try {
        if (isApprover) {
          await postFilterPreferenceApprover(postFilterPreferenceApproverAdapter(filters));
          setNewToast({
            type: 'SUCCESS',
            message: 'Filtros guardados con éxito.'
          });
        }

        if (!isApprover) {
          await postFilterPreference(postFiltersPreferencesAdapter(filters));
          setNewToast({
            type: 'SUCCESS',
            message: 'Filtros guardados con éxito.'
          });
        }
      } catch (err) {
        setNewToast({
          type: 'ERROR',
          message: err.message
        });
      }
    },
    [setNewToast, isApprover]
  );

  const handleSaveFilters = useCallback(
    (restfilters) => {
      window.localStorage.setItem(
        isApprover ? 'filtersApproverWorkReport' : 'filtersRequestGrid',
        JSON.stringify({ ...formikFilters.values, isApprover })
      );
      updateFiltersPreferences({ ...formikFilters.values, ...restfilters });
    },
    [formikFilters.values, isApprover, updateFiltersPreferences]
  );

  const handleClearFilters = useCallback(() => {
    window.localStorage.removeItem(isApprover ? 'filtersApproverWorkReport' : 'filtersRequestGrid');
    formikFilters.resetForm({
      values: {
        ...initialFiltersPrefrencesValues,
        isPending: formikFilters.values?.isPending
      }
    });
    setFiltersPreferencesState(initialFiltersPrefrencesValues);
  }, [formikFilters, initialFiltersPrefrencesValues, isApprover]);

  const handleChangeFilter = useCallback(
    (newFilters) => setFiltersPreferencesState(newFilters),
    []
  );

  useEffect(() => {
    if (filtersPreferences) {
      handleChangeFilter(filtersPreferences);
    }
  }, [filtersPreferences, handleChangeFilter]);

  const shouldSkipFilter = (filterKey, filterValue) =>
    !filterValue ||
    filterKey === 'isPending' ||
    filterKey === 'isApprover' ||
    (filterKey === 'asignacionEventoEnum' &&
      filtersPreferencesState.asignacionEventoEnum === eventAsignmentEnum.all);

  const handleArrayFilter = (filterKey, filterValue, index, arr) => {
    if (!filterValue.length) return;

    filterValue.forEach((v, idx) =>
      arr.push({ id: `${index}-${idx}`, filter: v?.nombre || v?.label, key: filterKey })
    );
  };

  const handleAsignacionEventoEnumFilter = (filterKey, index, arr) => {
    arr.push({
      id: index,
      filter: chipsWordsForEventAsignment[filtersPreferencesState.asignacionEventoEnum],
      key: filterKey
    });
  };

  const handleObjectFilter = (filterKey, filterValue, index, arr) => {
    arr.push({
      id: index,
      filter: filterValue?.abreviatura || filterValue?.nombre,
      key: filterKey
    });
  };

  const handleFechaDesdeFilter = (filterKey, index, arr) => {
    const [date] = filtersPreferencesState.fechaDesde.split('T');
    const [year, month, day] = date.split('-');
    const fechaDesde = [day, month, year].join('/');

    arr.push({
      id: index,
      filter: `Desde: ${fechaDesde}`,
      key: filterKey
    });
  };

  const handleFechaHastaFilter = (filterKey, index, arr) => {
    const [date] = filtersPreferencesState.fechaHasta.split('T');
    const [year, month, day] = date.split('-');
    const fechaHasta = [day, month, year].join('/');

    arr.push({
      id: index,
      filter: `Hasta: ${fechaHasta}`,
      key: filterKey
    });
  };

  const getAsigancionEventoEnum = (filterKey, index, arr) => {
    if (
      filterKey === 'asignacionEventoEnum' &&
      (filtersPreferencesState.asignacionEventoEnum === eventAsignmentEnum.assigned ||
        filtersPreferencesState.asignacionEventoEnum === eventAsignmentEnum.pending)
    ) {
      handleAsignacionEventoEnumFilter(filterKey, index, arr);
    }
  };

  const handleChips = useCallback(
    (handleChipsProps) => {
      const arr = [];

      Object.entries(filtersPreferencesState).forEach(([filterKey, filterValue], index) => {
        if (shouldSkipFilter(filterKey, filterValue)) return;

        if (filterKey === 'estadosSolicitud' && handleChipsProps?.isPending) {
          return;
        }

        if (Array.isArray(filterValue)) {
          handleArrayFilter(filterKey, filterValue, index, arr);

          return;
        }

        getAsigancionEventoEnum(filterKey, index, arr);

        if (typeof filterValue === 'object') {
          handleObjectFilter(filterKey, filterValue, index, arr);

          return;
        }

        if (filterKey === 'isPending') {
          return;
        }

        if (filterKey === 'fechaDesde') {
          handleFechaDesdeFilter(filterKey, index, arr);

          return;
        }

        if (filterKey === 'fechaHasta') {
          handleFechaHastaFilter(filterKey, index, arr);

          return;
        }

        arr.push({ id: index, filter: filterValue, key: filterKey });
      });

      return arr;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersPreferencesState]
  );

  const handleDeleteChips = useCallback(
    (filterObj) => {
      let newValue;
      let newFilterObject;

      if (Array.isArray(filtersPreferencesState[filterObj.key])) {
        newValue = filtersPreferencesState[filterObj.key].filter(
          (obj) => obj.label !== filterObj.filter
        );
      } else if (typeof filtersPreferencesState[filterObj.key] === 'object') {
        newValue = null;
      } else if (typeof filtersPreferencesState[filterObj.key] === 'string') {
        newValue = '';
      }
      if (filterObj.key === 'idsContratos') {
        newFilterObject = { idsContratos: [], posicionesLineas: [] };
      }
      if (filterObj.key === 'tipoUbicacionEnum') {
        newFilterObject = { unidadesNegocioIds: [], operacionesIds: [], ubicacion: [] };
      }
      if (filterObj.key === 'asignacionEventoEnum') {
        newValue = eventAsignmentEnum.all;
      }

      const newFilter = {
        ...filtersPreferencesState,
        ...newFilterObject,
        [filterObj.key]: newValue
      };

      formikFilters.resetForm({ values: newFilter });
      setFiltersPreferencesState(newFilter);
    },
    [filtersPreferencesState, formikFilters]
  );

  return (
    <FilterContext.Provider
      value={{
        filter: filtersPreferencesState,
        formikFilters,
        handleChangeFilter,
        handleChips,
        handleClearFilters,
        handleDeleteChips,
        handleSaveFilters
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

FilterProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default FilterProvider;
